import React, { useMemo } from 'react';
import { Button, Modal, Tree, Tag, Typography, Space } from 'antd';
import { FileOutlined, FolderOutlined, FileZipOutlined, WarningOutlined } from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';

const { Text } = Typography;

export interface ZipTreeEntry {
    key: string;
    name: string;
    path: string;
    isZip: boolean;
    isDirectory: boolean;
    children?: ZipTreeEntry[];
}

export interface ZipGroup {
    name: string;
    entries: ZipTreeEntry[];
}

interface NestedZipModalProps {
    visible: boolean;
    groups: ZipGroup[];
    loading: boolean;
    onExtractAll: () => void;
    onSkipInnerZips: () => void;
    onCancel: () => void;
}

function countFiles(entries: ZipTreeEntry[]): { files: number; zips: number } {
    let files = 0;
    let zips = 0;
    for (const entry of entries) {
        if (entry.isDirectory) continue;
        if (entry.isZip) {
            zips++;
        } else {
            files++;
        }
        if (entry.children) {
            const inner = countFiles(entry.children);
            files += inner.files;
            zips += inner.zips;
        }
    }
    return { files, zips };
}

function toTreeData(entries: ZipTreeEntry[]): DataNode[] {
    return entries.map(entry => {
        const isZip = entry.isZip;
        const isDir = entry.isDirectory;

        let icon: React.ReactNode;
        if (isZip) {
            icon = <FileZipOutlined style={{ color: '#fa8c16' }} />;
        } else if (isDir) {
            icon = <FolderOutlined />;
        } else {
            icon = <FileOutlined />;
        }

        const title = (
            <Space size={4}>
                <span>{entry.name}</span>
                {isZip && (
                    <Tag icon={<WarningOutlined />} color="warning" style={{ marginLeft: 4, fontSize: 11 }}>
                        ZIP
                    </Tag>
                )}
            </Space>
        );

        const node: DataNode = {
            key: entry.key,
            title,
            icon,
            children: entry.children ? toTreeData(entry.children) : undefined,
        };

        return node;
    });
}

function collectExpandKeys(entries: ZipTreeEntry[]): string[] {
    const keys: string[] = [];
    const collect = (nodes: ZipTreeEntry[]) => {
        for (const n of nodes) {
            if (n.children?.length) {
                keys.push(n.key);
                collect(n.children);
            }
        }
    };
    collect(entries);
    return keys;
}

const NestedZipModal: React.FC<NestedZipModalProps> = ({
    visible,
    groups,
    loading,
    onExtractAll,
    onSkipInnerZips,
    onCancel,
}) => {
    const totals = useMemo(() => {
        let files = 0;
        let zips = 0;
        for (const g of groups) {
            const c = countFiles(g.entries);
            files += c.files;
            zips += c.zips;
        }
        return { files, zips };
    }, [groups]);

    const groupedTreeData = useMemo(() => {
        return groups.map(g => ({
            name: g.name,
            treeData: toTreeData(g.entries),
            allKeys: collectExpandKeys(g.entries),
        }));
    }, [groups]);

    const archiveCount = groups.length;
    const title = archiveCount > 1 ? `Nested ZIPs Detected in ${archiveCount} Archives` : 'Nested ZIP Detected';

    return (
        <Modal
            title={title}
            open={visible}
            onCancel={onCancel}
            width={600}
            footer={[
                <Button key="cancel" onClick={onCancel} disabled={loading}>
                    Cancel
                </Button>,
                <Button key="skip" onClick={onSkipInnerZips} disabled={loading}>
                    Skip Inner ZIPs
                </Button>,
                <Button key="extract" type="primary" onClick={onExtractAll} loading={loading}>
                    Extract All
                </Button>,
            ]}
        >
            <div style={{ marginBottom: 16 }}>
                <Text>
                    {archiveCount > 1 ? (
                        <>
                            <Text strong>{archiveCount} uploaded archives</Text> contain nested ZIP files.
                        </>
                    ) : (
                        <>
                            The file <Text strong>{groups[0]?.name}</Text> contains nested ZIP files.
                        </>
                    )}{' '}
                    You can extract all files including those inside the inner ZIPs, or skip the inner ZIPs and only
                    process the top-level files.
                </Text>
            </div>

            <div style={{ marginBottom: 12 }}>
                <Space>
                    <Tag color="blue">
                        {totals.files} file{totals.files !== 1 ? 's' : ''}
                    </Tag>
                    <Tag color="warning" icon={<WarningOutlined />}>
                        {totals.zips} inner ZIP{totals.zips !== 1 ? 's' : ''}
                    </Tag>
                </Space>
            </div>

            <div
                style={{
                    maxHeight: 350,
                    overflow: 'auto',
                    border: '1px solid #f0f0f0',
                    borderRadius: 6,
                    padding: 8,
                    background: '#fafafa',
                }}
            >
                {groupedTreeData.map(g => (
                    <div key={g.name} style={archiveCount > 1 ? { marginBottom: 12 } : undefined}>
                        {archiveCount > 1 && (
                            <Text strong style={{ display: 'block', marginBottom: 4 }}>
                                {g.name}
                            </Text>
                        )}
                        <Tree
                            showIcon
                            defaultExpandAll
                            defaultExpandedKeys={g.allKeys}
                            treeData={g.treeData}
                            selectable={false}
                        />
                    </div>
                ))}
            </div>
        </Modal>
    );
};

export default NestedZipModal;
